/**
 * Embedded passwordless auth — the Paybyrd standard.
 *
 * Owns the SPA's auth state end-to-end: collects email in our own form,
 * triggers Auth0's /passwordless/start, parses the magic-link callback,
 * exchanges the code for tokens (PKCE), and refreshes silently before
 * each API call. No Auth0 hosted page is ever shown to the user.
 *
 * State machine:
 *   loading           -> on first mount, before we've checked storage
 *   unauthenticated   -> show LoginGate's email form
 *   link_sent         -> show "check your inbox"
 *   authenticated     -> render the app
 *   error             -> show whatever blew up
 *
 * Storage layout (localStorage):
 *   engage_auth         { refreshToken, accessToken, expiresAt, idToken }
 *   engage_login_state  { state, codeVerifier, email, createdAt } during the
 *                       round-trip from form submit to magic-link click. Lives
 *                       in localStorage (not sessionStorage) so the magic link
 *                       can be clicked in a different tab from the one that
 *                       sent it — which is the default behaviour of every
 *                       major email client. Auto-expires after 15 minutes.
 *
 * This file is designed to be lifted into other Paybyrd apps unchanged
 * (along with shared/auth/*). Replace the `engage_*` storage keys per app.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { setTokenGetter } from "../../lib/api";
import { pkcePair, randomToken } from "../auth/pkce";
import {
  decodeJwt,
  exchangeCode,
  passwordlessStart,
  refreshTokens,
  revokeRefreshToken,
  type Auth0Config,
  type TokenResponse,
} from "../auth/auth0Client";

const STORAGE_KEY = "engage_auth";
const LOGIN_STATE_KEY = "engage_login_state";
/** Magic links expire in 5 min on Auth0's side; we keep an extra buffer. */
const LOGIN_STATE_TTL_MS = 15 * 60 * 1000;
/** Refresh access token if it expires within this many ms. */
const REFRESH_LEEWAY_MS = 30_000;

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN as string | undefined;
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined;
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined;

export function isAuth0Configured(): boolean {
  return Boolean(AUTH0_DOMAIN && AUTH0_CLIENT_ID && AUTH0_AUDIENCE);
}

function authConfig(): Auth0Config {
  return {
    domain: AUTH0_DOMAIN!,
    clientId: AUTH0_CLIENT_ID!,
    audience: AUTH0_AUDIENCE!,
  };
}

// ── Persisted shape ─────────────────────────────────────────────────────────

interface StoredAuth {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
  expiresAt: number; // unix ms
}

function loadStored(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuth;
    if (!parsed.accessToken || !parsed.refreshToken || !parsed.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(auth: StoredAuth): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
}

function clearStored(): void {
  localStorage.removeItem(STORAGE_KEY);
}

function tokenResponseToStored(prev: StoredAuth | null, t: TokenResponse): StoredAuth {
  return {
    accessToken: t.access_token,
    // Auth0 returns a fresh refresh_token on rotation; fall back to previous
    // if the response omits one (some flows do).
    refreshToken: t.refresh_token ?? prev?.refreshToken ?? "",
    idToken: t.id_token ?? prev?.idToken,
    expiresAt: Date.now() + t.expires_in * 1000,
  };
}

interface LoginState {
  state: string;
  codeVerifier: string;
  email: string;
  createdAt: number;
}

function loadLoginState(): LoginState | null {
  try {
    const raw = localStorage.getItem(LOGIN_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LoginState;
    // Drop stale entries — the magic link itself expires after 5 min on
    // Auth0's side, so a state that's 15 min old can't possibly succeed.
    if (Date.now() - (parsed.createdAt ?? 0) > LOGIN_STATE_TTL_MS) {
      localStorage.removeItem(LOGIN_STATE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveLoginState(s: LoginState): void {
  localStorage.setItem(LOGIN_STATE_KEY, JSON.stringify(s));
}

function clearLoginState(): void {
  localStorage.removeItem(LOGIN_STATE_KEY);
}

// ── User claims (display only) ──────────────────────────────────────────────

interface UserClaims {
  email?: string;
  name?: string;
  sub?: string;
}

function userFromIdToken(idToken: string | undefined): UserClaims | null {
  if (!idToken) return null;
  const claims = decodeJwt<{ email?: string; name?: string; sub?: string }>(idToken);
  if (!claims) return null;
  return { email: claims.email, name: claims.name, sub: claims.sub };
}

// ── Context ─────────────────────────────────────────────────────────────────

type AuthStatus = "loading" | "unauthenticated" | "link_sent" | "authenticated" | "error";

interface AuthContextValue {
  status: AuthStatus;
  user: UserClaims | null;
  error: string | null;
  /** Email the magic link was just sent to (only meaningful when status === "link_sent"). */
  pendingEmail: string | null;
  startLogin: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  resetLogin: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

// ── Provider ────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!isAuth0Configured()) {
    return <DevModeProvider>{children}</DevModeProvider>;
  }
  return <Auth0PasswordlessProvider>{children}</Auth0PasswordlessProvider>;
}

/**
 * Pass-through context for local dev (no Auth0 vars set). lib/api.ts falls
 * back to reading engage_dev_key from localStorage on its own.
 */
function DevModeProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthContextValue>(
    () => ({
      status: "authenticated",
      user: { email: "dev@local" },
      error: null,
      pendingEmail: null,
      startLogin: async () => {},
      logout: async () => {},
      getAccessToken: async () => localStorage.getItem("engage_dev_key") ?? "",
      resetLogin: () => {},
    }),
    [],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function Auth0PasswordlessProvider({ children }: { children: ReactNode }) {
  const cfg = useMemo(authConfig, []);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const storedRef = useRef<StoredAuth | null>(loadStored());
  const [user, setUser] = useState<UserClaims | null>(
    () => userFromIdToken(storedRef.current?.idToken),
  );

  // Concurrent refresh dedupe.
  const refreshInflight = useRef<Promise<string> | null>(null);

  const setAuthenticated = useCallback((stored: StoredAuth) => {
    storedRef.current = stored;
    persist(stored);
    setUser(userFromIdToken(stored.idToken));
    setStatus("authenticated");
    setError(null);
  }, []);

  const clearAuth = useCallback(() => {
    storedRef.current = null;
    clearStored();
    clearLoginState();
    setUser(null);
  }, []);

  // ── Token getter (refreshes if near-expiry) ─────────────────────────────
  //
  // Failure modes, ordered by user impact:
  //
  //   1. No stored auth at all -> return "", let the API 401 itself.
  //      AuthProvider's status is already "unauthenticated", LoginGate
  //      handles the UX.
  //   2. Access token still good -> return it.
  //   3. Access token near/past expiry, no refresh_token (e.g. Auth0's
  //      "Allow Offline Access" was off when this user logged in) ->
  //      return the stale token. The API call may 401, the user sees
  //      an error banner via ApiErrorBanner, but they stay on the page.
  //      They'll re-authenticate manually when they notice.
  //   4. Refresh token exists but refresh call fails with anything other
  //      than a network blip -> session is dead, clear auth, kick to
  //      LoginGate.
  //
  // The previous version conflated 3 and 4 — any refresh failure
  // (including "no refresh_token to send") tripped a hard logout, so
  // navigating to a protected page silently dumped the user back to
  // the sign-in form. That's the bug this edit fixes.
  const getAccessToken = useCallback(async (): Promise<string> => {
    const cur = storedRef.current;
    if (!cur) return "";
    if (cur.expiresAt - Date.now() > REFRESH_LEEWAY_MS) return cur.accessToken;
    if (!cur.refreshToken) {
      // Path 3 — can't refresh; hand back what we have and let the API 401.
      console.warn("[engage-auth] access token near-expiry but no refresh token (Allow Offline Access?) — returning stale token");
      return cur.accessToken;
    }

    if (refreshInflight.current) return refreshInflight.current;
    refreshInflight.current = (async () => {
      try {
        const next = await refreshTokens(cfg, cur.refreshToken);
        const merged = tokenResponseToStored(cur, next);
        storedRef.current = merged;
        persist(merged);
        if (next.id_token) setUser(userFromIdToken(next.id_token));
        return merged.accessToken;
      } catch (e) {
        // Path 4 — refresh failed. Session is dead.
        console.warn("[engage-auth] refresh failed; clearing auth", e);
        clearAuth();
        setStatus("unauthenticated");
        // Don't re-throw; return the stale access token so the in-flight
        // request can complete (and fail cleanly) instead of throwing
        // up the call stack to React event handlers.
        return cur.accessToken;
      } finally {
        refreshInflight.current = null;
      }
    })();
    return refreshInflight.current;
  }, [cfg, clearAuth]);

  // Plumb the getter into the fetch wrapper exactly once.
  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // ── Boot: handle magic-link callback OR resume session ──────────────────
  //
  // Idempotency notes:
  //   - In React StrictMode (dev), this effect runs twice. The first run
  //     consumes the OAuth code (single-use) and may persist tokens. The
  //     second run hits a 4xx from /oauth/token because the code is gone.
  //   - We persist tokens to localStorage BEFORE checking the `cancelled`
  //     flag so a remount can read them. On the second run, if the
  //     exchange fails but we find tokens in storage, we authenticate
  //     from those instead of showing an error.
  //   - This also recovers the user gracefully from a page refresh that
  //     happens to land while the URL still carries `?code=&state=`.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      const stateParam = url.searchParams.get("state");
      const errParam = url.searchParams.get("error");
      const errDesc = url.searchParams.get("error_description");

      const cleanUrl = () => {
        url.searchParams.delete("code");
        url.searchParams.delete("state");
        url.searchParams.delete("error");
        url.searchParams.delete("error_description");
        window.history.replaceState({}, "", url.toString());
      };

      // 1. Auth0 returned an error after the user clicked the link.
      if (errParam) {
        console.warn("[engage-auth] auth0 returned error:", errParam, errDesc);
        cleanUrl();
        if (!cancelled) {
          setError(errDesc || errParam);
          setStatus("error");
        }
        return;
      }

      // 2. Magic-link callback — try to exchange the code for tokens.
      if (code && stateParam) {
        const login = loadLoginState();

        if (!login || login.state !== stateParam) {
          // Different browser, link expired, or storage cleared between
          // form submit and click. With localStorage-backed state, this
          // mostly means cross-browser (mobile click → desktop browser).
          console.warn(
            "[engage-auth] state mismatch — login_state:",
            login ? "present (state=" + login.state + ")" : "absent",
            "url state:", stateParam,
          );
          cleanUrl();
          if (!cancelled) {
            setError(
              "This sign-in link wasn't started in this browser. " +
              "Open the link in the same browser where you entered your email, " +
              "or request a new one.",
            );
            setStatus("error");
          }
          return;
        }

        try {
          const tokens = await exchangeCode(
            cfg, code, login.codeVerifier, window.location.origin,
          );
          const merged = tokenResponseToStored(null, tokens);
          // Persist FIRST (unguarded) so a concurrent mount can recover.
          persist(merged);
          clearLoginState();
          cleanUrl();
          console.info("[engage-auth] code exchange succeeded; tokens stored");
          if (!cancelled) {
            storedRef.current = merged;
            setUser(userFromIdToken(merged.idToken));
            setStatus("authenticated");
            setError(null);
          }
        } catch (e) {
          // Exchange failed. Two common reasons:
          //   a) The code was already consumed by another mount (StrictMode).
          //      In that case localStorage has fresh tokens — use them.
          //   b) The code is genuinely invalid/expired — show an error.
          const stored = loadStored();
          if (stored && stored.expiresAt - Date.now() > 0) {
            console.info(
              "[engage-auth] code exchange failed, but found fresh tokens in storage — using those",
            );
            cleanUrl();
            if (!cancelled) {
              storedRef.current = stored;
              setUser(userFromIdToken(stored.idToken));
              setStatus("authenticated");
              setError(null);
            }
            return;
          }
          console.error("[engage-auth] code exchange failed:", e);
          cleanUrl();
          if (!cancelled) {
            setError((e as Error).message);
            setStatus("error");
          }
        }
        return;
      }

      // 3. No callback — try to resume from stored tokens.
      const stored = storedRef.current ?? loadStored();
      if (!stored) {
        if (!cancelled) setStatus("unauthenticated");
        return;
      }

      // Access token still good — use it.
      if (stored.expiresAt - Date.now() > REFRESH_LEEWAY_MS) {
        storedRef.current = stored;
        if (!cancelled) {
          setUser(userFromIdToken(stored.idToken));
          setStatus("authenticated");
        }
        return;
      }

      // Otherwise refresh.
      try {
        const next = await refreshTokens(cfg, stored.refreshToken);
        const merged = tokenResponseToStored(stored, next);
        persist(merged);
        if (!cancelled) {
          storedRef.current = merged;
          setUser(userFromIdToken(merged.idToken));
          setStatus("authenticated");
        }
      } catch {
        clearAuth();
        if (!cancelled) setStatus("unauthenticated");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cfg, clearAuth, setAuthenticated]);

  // ── startLogin: send magic link ─────────────────────────────────────────
  const startLogin = useCallback(
    async (email: string) => {
      setError(null);
      const trimmed = email.trim();
      const { codeVerifier, codeChallenge } = await pkcePair();
      const state = randomToken();
      saveLoginState({ state, codeVerifier, email: trimmed, createdAt: Date.now() });
      try {
        await passwordlessStart(cfg, {
          email: trimmed,
          state,
          codeChallenge,
          redirectUri: window.location.origin,
        });
        setPendingEmail(trimmed);
        setStatus("link_sent");
      } catch (e) {
        clearLoginState();
        setError((e as Error).message);
        throw e;
      }
    },
    [cfg],
  );

  // ── logout ──────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    const stored = storedRef.current;
    if (stored?.refreshToken) {
      await revokeRefreshToken(cfg, stored.refreshToken);
    }
    clearAuth();
    setStatus("unauthenticated");
    setPendingEmail(null);
    setError(null);
  }, [cfg, clearAuth]);

  const resetLogin = useCallback(() => {
    setError(null);
    setPendingEmail(null);
    setStatus("unauthenticated");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, error, pendingEmail, startLogin, logout, getAccessToken, resetLogin }),
    [status, user, error, pendingEmail, startLogin, logout, getAccessToken, resetLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
